import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/cn.js';
import { hasPermission, useUserPermissions } from './Guard.js';

export interface SidebarSection {
  label: string;
  items: SidebarItem[];
}

export interface SidebarItem {
  label: string;
  to: string;
  /**
   * V1.2 RBAC — when set, the nav item is hidden for users who lack the
   * permission. Server-side `authorize()` middleware enforces the same
   * gate; this is UI-only (so users don't click into a 403).
   */
  viewId?: string;
  /**
   * V2.1-H — small icon left of the label. ~14px display size, stroke
   * 1.6, no fill. Pass an inline SVG element.
   */
  icon?: ReactNode;
  /**
   * V2.1-H — canonical numeric badge (matches the design ref). When
   * count is `0` or omitted the badge stays hidden. Inactive state
   * renders the count as plain ink-3 numerals; active state inverts to
   * a pill (primary bg + ink-inv text).
   */
  badgeCount?: number;
  /**
   * V1.4 scope Z — escape-hatch for fully custom badge content. Wins
   * over `badgeCount`. Existing consumers pass arbitrary ReactNode.
   */
  badge?: ReactNode;
}

interface SidebarProps {
  /** Brand title — typically "JYWER" or "JYWER Admin". */
  title: string;
  /** V2.1-H — subtitle under the title (e.g. "Internal workspace"). */
  subtitle?: string;
  /**
   * V2.1-H — top-of-sidebar icon left of the title. Defaults to a
   * primary-tinted rounded-square mark when omitted.
   */
  brandIcon?: ReactNode;
  sections: SidebarSection[];
  footer?: ReactNode;
}

export function Sidebar({ title, subtitle, brandIcon, sections, footer }: SidebarProps) {
  const perms = useUserPermissions();
  const filteredSections = sections
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => !i.viewId || hasPermission(perms, i.viewId)),
    }))
    .filter((s) => s.items.length > 0);
  return (
    <aside className="flex h-screen w-60 flex-col border-r border-line-1 bg-paper-1">
      <div className="flex items-center gap-2 px-4 pb-4 pt-5">
        {brandIcon ?? <DefaultBrandMark />}
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-h3 font-semibold text-ink-0 tracking-[-0.015em]">
            {title}
          </span>
          {subtitle ? <span className="truncate text-meta text-ink-3">{subtitle}</span> : null}
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {filteredSections.map((section) => (
          <div key={section.label} className="mb-5">
            <div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-3">
              {section.label}
            </div>
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-2.5 rounded-token-sm px-2 py-1.5 text-ui font-medium text-ink-1 transition-colors duration-1 ease-brand',
                        'hover:bg-paper-2 hover:text-ink-0',
                        'focus-visible:shadow-focus focus-visible:outline-none',
                        isActive &&
                          'bg-primary-tint text-primary hover:bg-primary-tint hover:text-primary',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {item.icon ? (
                          <span
                            className={cn(
                              'flex h-3.5 w-3.5 shrink-0 items-center justify-center text-ink-2 transition-colors duration-1 ease-brand',
                              'group-hover:text-ink-1',
                              isActive && 'text-primary group-hover:text-primary',
                            )}
                            aria-hidden="true"
                          >
                            {item.icon}
                          </span>
                        ) : null}
                        <span className="truncate">{item.label}</span>
                        <SidebarBadge
                          isActive={isActive}
                          badgeCount={item.badgeCount}
                          badge={item.badge}
                        />
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {footer ? <div className="border-t border-line-1 px-4 py-3">{footer}</div> : null}
    </aside>
  );
}

/**
 * V2.1-H — sidebar badge.
 *
 * Custom `badge` ReactNode wins (V1.4-Z escape hatch). Otherwise a
 * numeric `badgeCount` ≥ 1 renders. Two visual states: inactive =
 * tabular ink-3 numerals (no background); active = primary-bg pill
 * with ink-inv text — matches the design ref.
 */
function SidebarBadge({
  isActive,
  badgeCount,
  badge,
}: {
  isActive: boolean;
  badgeCount?: number;
  badge?: ReactNode;
}) {
  if (badge) return <span className="ml-auto inline-flex items-center">{badge}</span>;
  if (!badgeCount || badgeCount <= 0) return null;
  return (
    <span
      className={cn(
        'ml-auto inline-flex items-center justify-center text-[11px] font-medium tabular-nums leading-none',
        isActive
          ? 'h-[18px] min-w-[22px] rounded-token-pill bg-primary px-1.5 text-ink-inv'
          : 'text-ink-3',
      )}
    >
      {badgeCount}
    </span>
  );
}

/**
 * V2.1-H — default brand-mark when consumer doesn't supply one.
 * Rounded square in primary, with the design ref's notch silhouette.
 */
function DefaultBrandMark() {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-token-sm bg-primary text-ink-inv"
      aria-hidden="true"
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 3H13V8H8V13H3V3Z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
